import { useEffect, useState } from 'react'
import { Link } from 'wouter'
import { BackdropOrbs } from '../components/BackdropOrbs'
import { SiteFooter } from '../components/SiteFooter'
import { CharacterImage } from '../components/CharacterImage'
import { ApiError } from '../lib/api'
import { fetchCharacters, linkLabel, type PublicCharacter } from '../lib/characters'

/** The community character board: everyone's System 404 originals. Cards link
 *  to the detail page, and each card carries its outbound links directly so
 *  visitors can jump straight to chub/discord/wherever. */

function CharacterCard({ c }: { c: PublicCharacter }) {
  return (
    <div className="group overflow-hidden rounded-lg border border-edge bg-void-2/60 transition hover:-translate-y-0.5 hover:border-signal/50">
      <Link href={`/characters/${c.id}`} aria-label={`Open ${c.title}`}>
        <CharacterImage src={c.imageUrl} alt={c.title} />
      </Link>
      {c.links.length > 0 && (
        <div className="flex flex-col">
          {c.links.map((url) => (
            <a
              key={url}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="block border-b border-edge bg-void-2 px-3 py-2.5 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 transition hover:bg-signal/10 hover:text-signal"
            >
              {linkLabel(url)}
            </a>
          ))}
        </div>
      )}
      <div className="p-4">
        <Link
          href={`/characters/${c.id}`}
          className="font-display font-semibold text-white transition group-hover:text-signal"
        >
          {c.title}
        </Link>
        <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-xs leading-relaxed text-slate-400">
          {c.tagline}
        </p>
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
          <span className="truncate">{c.creator ? `by ${c.creator}` : 'anonymous'}</span>
          <span className="flex shrink-0 items-center gap-2">
            {c.lorebook && (
              <span className="rounded-full border border-signal/30 px-1.5 py-0.5 text-signal/80">
                Entry
              </span>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

export function Characters() {
  const [chars, setChars] = useState<PublicCharacter[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    fetchCharacters()
      .then((res) => alive && setChars(res.characters))
      .catch((err) => {
        if (!alive) return
        setError(
          err instanceof ApiError && err.status === 503
            ? 'The archive is offline. The database is not configured yet.'
            : 'Could not reach the archive. Try again in a moment.',
        )
      })
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className="lorebook-root flex min-h-screen flex-col">
      <BackdropOrbs />
      <header className="sticky top-0 z-30 border-b border-edge bg-void/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 lg:px-6">
          <div className="flex items-center gap-3">
            <Link href="/lorebook" className="text-sm text-slate-300 transition hover:text-signal">
              ← Lorebook
            </Link>
            <span className="text-slate-700">/</span>
            <span className="font-display text-sm text-white">Characters</span>
          </div>
          <Link
            href="/characters/new"
            className="rounded-full border border-signal/50 bg-signal/10 px-4 py-1.5 text-sm text-signal transition hover:bg-signal/20 hover:text-white"
          >
            + New character
          </Link>
        </div>
      </header>

      <div className="mx-auto w-full max-w-6xl flex-1 px-4 py-10 lg:px-6">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.35em] text-signal/70">Characters</div>
          <h1 className="mt-2 font-display text-4xl font-semibold text-white">
            Survivors of System 404
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Original characters built by the community for the System 404 setting. Each one can
            carry a short lorebook entry so your RP partners know exactly who just walked aboard.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-sos/30 bg-sos/5 p-6 text-sm text-slate-300">
            {error}
          </div>
        )}

        {!error && chars === null && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-lg border border-edge bg-void-2/60">
                <div className="aspect-[3/4] w-full border-b border-edge bg-void-2" />
                <div className="space-y-2 p-4">
                  <div className="h-4 w-2/3 rounded bg-void" />
                  <div className="h-3 w-full rounded bg-void" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!error && chars !== null && chars.length === 0 && (
          <div className="rounded-lg border border-edge bg-void-2/60 p-10 text-center">
            <div className="font-display text-lg text-white">Nobody has filed a character yet.</div>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
              The archive is empty and the void is hiring. Create the first resident of System 404.
            </p>
            <Link
              href="/characters/new"
              className="mt-6 inline-block rounded-full border border-signal/50 bg-signal/10 px-5 py-2 text-sm text-signal transition hover:bg-signal/20 hover:text-white"
            >
              Create a character
            </Link>
          </div>
        )}

        {!error && chars !== null && chars.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-5">
            {chars.map((c) => (
              <CharacterCard key={c.id} c={c} />
            ))}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  )
}
